import React from "react";
import { toast } from "react-toastify";
import moment from "moment";
import {
    Add,
    ModeEditOutlineTwoTone,
    CheckCircleOutline,
    CancelOutlined,
} from "@mui/icons-material";
import {
    Box,
    Button,
    IconButton,
    Switch,
    Typography,
    Tooltip,
} from "@mui/material";

import AppTable from "components/Shared/AppTable";
import FormDrawer from "components/Shared/FormDrawer";
import { useVendorProfileDetailContext } from "contexts/VendorProfileDetailContext";
import {
    useCreateVendorProfileLimitMutation,
    useUpdateVendorProfileLimitMutation,
} from "store/services/vendorProfileApi";
import VendorProfileLimitForm from "../Components/VendorProfileLimitForm";
import { useAuth } from "hooks";

const LimitsTab = ({ vendorId }) => {
    const { isSystemAdmin, isAccountAdmin } = useAuth();
    const canEdit = isSystemAdmin || isAccountAdmin;

    const [showForm, setShowForm] = React.useState(false);
    const [isEditMode, setIsEditMode] = React.useState(false);

    const { vendorProfileDetail, loadingvendorProfileDetail } =
        useVendorProfileDetailContext();

    const vendorLimitData = vendorProfileDetail?.vendorLimits;
    const hasExistingLimit = !!vendorLimitData;

    const [addLimit, { isLoading: adding }] =
        useCreateVendorProfileLimitMutation();
    const [updateLimit, { isLoading: updating }] =
        useUpdateVendorProfileLimitMutation();

    // Open Add/Edit Form
    const handleOpenForm = (isEdit = false) => {
        setIsEditMode(isEdit);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setIsEditMode(false);
        setShowForm(false);
    };

    // Add Limit (Create)
    const handleAddLimit = async (payload) => {
        try {
            await addLimit(payload).unwrap();
            toast.success("Vendor limit added successfully!");
            handleCloseForm();
        } catch (err) {
            toast.error(err?.data?.message || "Couldn't add vendor limit!");
        }
    };

    // Update Limit
    const handleUpdateLimit = async (payload) => {
        try {
            await updateLimit({
                vendorId: payload.vendorId,
                creditLimit: payload.creditLimit,
                codLimit: payload.codLimit,
                isActive: payload.isActive,
            }).unwrap();
            toast.success("Vendor limit updated successfully!");
            handleCloseForm();
        } catch (err) {
            toast.error(err?.data?.message || "Couldn't update vendor limit!");
        }
    };

    // Toggle Active Status
    const handleToggleActive = async () => {
        if (!vendorLimitData) return;

        try {
            const newActive = !vendorLimitData.isActive;

            await updateLimit({
                vendorId,
                isActive: newActive,
                creditLimit: vendorLimitData.creditLimit,
                codLimit: vendorLimitData.codLimit,
            }).unwrap();

            toast.success(
                newActive
                    ? "Vendor limit activated successfully!"
                    : "Vendor limit deactivated successfully!"
            );
        } catch (err) {
            toast.error(err?.data?.message || "Failed to update status!");
        }
    };

    const columns = [
        {
            name: "Credit Limit",
            selector: (row) => row?.creditLimit,
            cell: (row) => (
                <div>
                    {typeof row?.creditLimit === "number"
                        ? row.creditLimit.toFixed(2)
                        : "-"}
                </div>
            ),
            minWidth: "150px",
        },
        {
            name: "Credit Amount",
            selector: () => vendorProfileDetail?.balance?.totalUnpaidCharges,
            cell: () => (
                <div>
                    {typeof vendorProfileDetail?.balance?.totalUnpaidCharges === "number"
                        ? vendorProfileDetail.balance.totalUnpaidCharges.toFixed(2)
                        : "N/A"}
                </div>
            ),
            minWidth: "150px",
        },
        {
            name: "COD Limit",
            selector: (row) => row?.codLimit,
            cell: (row) => (
                <div>
                    {typeof row?.codLimit === "number"
                        ? row.codLimit.toFixed(2)
                        : "-"}
                </div>
            ),
            minWidth: "150px",
        },
        {
            name: "Created By",
            selector: (row) => row?.createdBy?.name ?? "-",
            cell: (row) => (
                <Tooltip title={row?.createdBy?.email ?? ""} arrow>
                    <div>{row?.createdBy?.name ?? "-"}</div>
                </Tooltip>
            ),
            minWidth: "150px",
        },
        {
            name: "Updated By",
            selector: (row) => row?.updatedBy?.name ?? "-",
            cell: (row) => (
                <Tooltip title={row?.updatedBy?.email ?? ""} arrow>
                    <div>{row?.updatedBy?.name ?? "-"}</div>
                </Tooltip>
            ),
            minWidth: "150px",
        },
        {
            name: "Created At",
            selector: (row) =>
                row?.createdAt ? moment(row.createdAt).format("YYYY-MM-DD HH:mm") : "-",
            cell: (row) => (
                <div>
                    {row?.createdAt
                        ? moment(row.createdAt).format("YYYY-MM-DD HH:mm")
                        : "-"}
                </div>
            ),
            minWidth: "170px",
        },
        {
            name: "Updated At",
            selector: (row) =>
                row?.updatedAt ? moment(row.updatedAt).format("YYYY-MM-DD HH:mm") : "-",
            cell: (row) => (
                <div>
                    {row?.updatedAt
                        ? moment(row.updatedAt).format("YYYY-MM-DD HH:mm")
                        : "-"}
                </div>
            ),
            minWidth: "170px",
        },
        ...(canEdit
            ? [
                {
                    name: "Status",
                    cell: (row) => (
                        <Box display="flex" alignItems="center" gap={1}>
                            {row.isActive ? (
                                <CheckCircleOutline
                                    color="success"
                                    fontSize="small"
                                />
                            ) : (
                                <CancelOutlined color="error" fontSize="small" />
                            )}
                            <Typography
                                variant="body2"
                                color={row.isActive ? "success.main" : "error.main"}
                                fontWeight={500}
                            >
                                {row.isActive ? "Active" : "Inactive"}
                            </Typography>
                            <Switch
                                checked={row.isActive ?? false}
                                onChange={handleToggleActive}
                                color="primary"
                                size="small"
                                disabled={updating}
                            />
                        </Box>
                    ),
                    ignoreRowClick: true,
                    minWidth: "200px",
                },
                {
                    name: "Actions",
                    cell: (row) => (
                        <Tooltip title="Edit Limit" arrow>
                            <IconButton
                                size="small"
                                onClick={() => handleOpenForm(true)}
                                disabled={updating || adding}
                            >
                                <ModeEditOutlineTwoTone />
                            </IconButton>
                        </Tooltip>
                    ),
                    ignoreRowClick: true,
                    right: true,
                    minWidth: "100px",
                },
            ]
            : []),
    ];

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" padding={2}>
                <Typography variant="h6" fontWeight={600}>
                    Vendor Limits
                </Typography>
                {!hasExistingLimit && canEdit && (
                    <Button
                        startIcon={<Add />}
                        variant="outlined"
                        onClick={() => handleOpenForm(false)}
                        disabled={adding || updating}
                    >
                        Add Limit
                    </Button>
                )}
            </Box>

            <AppTable
                columns={columns}
                data={vendorLimitData ? [vendorLimitData] : []}
                progressPending={loadingvendorProfileDetail}
                pagination={false}
                noDataComponent={
                    <Box py={4}>
                        <Typography variant="body2" color="text.secondary">
                            No limit set for this vendor.
                            {canEdit && " Click 'Add Limit' to create one."}
                        </Typography>
                    </Box>
                }
            />

            <FormDrawer open={showForm} onClose={handleCloseForm}>
                <VendorProfileLimitForm
                    vendorLimitDetail={isEditMode ? vendorLimitData : null}
                    vendorId={vendorId}
                    isBusy={adding || updating}
                    onSubmit={isEditMode ? handleUpdateLimit : handleAddLimit}
                    onCancel={handleCloseForm}
                />
            </FormDrawer>
        </>
    );
};

export default LimitsTab;